import React from 'react';
import { useDispatch } from 'react-redux';
import { Backdrop, CircularProgress, Grid } from '@mui/material';

import {
	DateField,
	ModuleSearchField,
	SearchSelectField,
	useCancelablePromise,
	useMountEffect,
	useSettings,
	Utils
} from 'app/main/components';
import { type DetailsSectionInfoContentProps } from 'app/main/components/page-layouts';

type OrdersDetailsSectionInfoProps = DetailsSectionInfoContentProps & { isAdd?: boolean };

export default function OrdersDetailsSectionInfo(props: OrdersDetailsSectionInfoProps) {
	const dispatch = useDispatch();
	const { t } = useSettings();
	const getCancelablePromise = useCancelablePromise();

	const canEditFull = Utils.getConnectionFromToken() !== 'PERCEPTIVE_CONTROL_BEB';

	const [isLoading, setIsLoading] = React.useState(false);

	const setCompaniesDefaults = React.useCallback(
		company => {
			props.sendChange('ords_comp_title', company?.comp_title || null);
			props.sendChange('ords_comp_address', company?.comp_address || null);
			props.sendChange('ords_comp_city', company?.comp_city || null);
			props.sendChange('ords_comp_zip', company?.comp_zip || null);
			props.sendChange('ords_comp_dist_id', company?.comp_dist_id || null);
			props.sendChange('ords_comp_dist_iso', company?.dist_iso || null);
			props.sendChange('ords_comp_regi_id', company?.comp_regi_id || null);
			props.sendChange('ords_comp_ctry_id', company?.comp_ctry_id || null);
			props.sendChange('ords_comp_ctry_title', company?.ctry_title || null);

			props.sendChange('ords_bill_comp_uid', company?.comp_uid || null);
			props.sendChange('ords_bill_comp_title', company?.comp_title || null);
			props.sendChange('ords_bill_comp_address', company?.comp_address || null);
			props.sendChange('ords_bill_comp_city', company?.comp_city || null);
			props.sendChange('ords_bill_comp_zip', company?.comp_zip || null);
			props.sendChange('ords_bill_comp_dist_id', company?.comp_dist_id || null);
			props.sendChange('ords_bill_comp_dist_iso', company?.dist_iso || null);
			props.sendChange('ords_bill_comp_regi_id', company?.comp_regi_id || null);
			props.sendChange('ords_bill_comp_ctry_id', company?.comp_ctry_id || null);
			props.sendChange('ords_bill_comp_ctry_title', company?.ctry_title || null);

			props.sendChange('ords_shad_uid', null);
			props.sendChange('ords_shad_title', company?.comp_title || null);
			props.sendChange('ords_shad_address', company?.comp_address || null);
			props.sendChange('ords_shad_city', company?.comp_city || null);
			props.sendChange('ords_shad_zip', company?.comp_zip || null);
			props.sendChange('ords_shad_dist_id', company?.comp_dist_id || null);
			props.sendChange('ords_shad_dist_iso', company?.dist_iso || null);
			props.sendChange('ords_shad_regi_id', company?.comp_regi_id || null);
			props.sendChange('ords_shad_ctry_id', company?.comp_ctry_id || null);
			props.sendChange('ords_shad_ctry_title', company?.ctry_title || null);

			props.sendChange('ords_noteshtml', company?.comp_ords_noteshtml || null);
			props.sendChange('ords_notes', company?.comp_ords_notes || null);

			props.sendChange('ords_user_assigned_uid', company?.comp_user_assigned_uid || null);
			props.sendChange('ords_user_assigned_2_uid', company?.comp_user_assigned_2_uid || null);

			if (canEditFull && company?.comp_core_id !== 2) {
				props.sendChange('ords_pric_id', null);
				props.sendChange('ords_disc_id', null);
				props.sendChange('ords_pate_id', 110);
				props.sendChange('ords_pame_id', 20);
				props.sendChange('ords_shme_id', null);
				props.sendChange('ords_shag_id', null);
			} else {
				props.sendChange('ords_pric_id', company?.comp_pric_id || null);
				props.sendChange('ords_disc_id', company?.comp_disc_id || null);
				props.sendChange('ords_pate_id', company?.comp_pate_id || null);
				props.sendChange('ords_pame_id', company?.comp_pame_id || null);
				props.sendChange('ords_shme_id', company?.comp_shme_id || null);
				props.sendChange('ords_shag_id', company?.comp_shag_id || null);
			}

			props.sendChange('comp_core_id', company?.comp_core_id);
		},
		[canEditFull, props.sendChange]
	);

	useMountEffect(() => {
		if (props.isAdd && !props.model._isCopy && props.model.ords_comp_uid) {
			setIsLoading(true);
			Utils.executeStored({
				action: 'st_companies_get_details',
				params: { comp_uid: props.model.ords_comp_uid },
				dispatch,
				getCancelablePromise,
				successHandler: ({ data }) => {
					setCompaniesDefaults(data);
					setIsLoading(false);
				}
			});
		}
	});

	return (
		<>
			<Backdrop open={isLoading}>
				<CircularProgress disableShrink />
			</Backdrop>
			{/* COMPANY */}
			<Grid size={12}>
				<ModuleSearchField
					name="ords_comp_uid"
					label={t('DETAILS_ORDERS_FIELD_COMPANY')}
					module="companies"
					uid={props.model.ords_comp_uid}
					filters={[
						{ filt_field: 'comp_blocked', filt_clause: 'not_equal', filt_value: 1 },
						{ filt_field: 'comp_core_id', filt_clause: 'in', filt_value: [1, 2, 5] }
					]}
					isEdit={props.isEdit && canEditFull}
					sendChange={props.sendChange}
					afterChange={setCompaniesDefaults}
					logProps={props.logProps}
					required
					validationError={t('ERROR_TEXT_REQUIRED')}
				/>
			</Grid>
			{/* STATUS */}
			<Grid size={{ sm: 6, xs: 12 }}>
				<SearchSelectField
					name="ords_orst_id"
					label={t('DETAILS_ORDERS_FIELD_STATUS')}
					systable="orders_statuses"
					value={props.model.ords_orst_id}
					isEdit={props.isEdit && canEditFull}
					sendChange={props.sendChange}
					logProps={props.logProps}
				/>
			</Grid>
			{/* SIGN DATE */}
			<Grid size={{ md: 3, sm: 6, xs: 12 }}>
				<DateField
					name="ords_d_sign"
					label={t('DETAILS_ORDERS_FIELD_SIGNDATE')}
					value={props.model.ords_d_sign}
					isEdit={props.isEdit}
					sendChange={props.sendChange}
					logProps={props.logProps}
					required
					validationError={t('ERROR_TEXT_REQUIRED')}
				/>
			</Grid>
			{/* REQUESTED DELIVERY DATE */}
			<Grid size={{ md: 3, sm: 6, xs: 12 }}>
				<DateField
					name="ords_d_delivery_requested"
					label={t('DETAILS_ORDERS_FIELD_DATE_DATE_REQDELIVERY')}
					value={props.model.ords_d_delivery_requested}
					isEdit={props.isEdit}
					sendChange={props.sendChange}
					logProps={props.logProps}
					required={!canEditFull}
					validations={{
						minDate: props.model.ords_d_sign
					}}
					validationErrors={{
						isDefaultRequiredValue: t('ERROR_TEXT_REQUIRED'),
						minDate: t('ERROR_DELIVERY_DATE_REQUESTED_MIN_VALUE')
					}}
				/>
			</Grid>
			{/* ASSIGNED USER */}
			<Grid size={{ sm: 6, xs: 12 }}>
				<ModuleSearchField
					name="ords_user_assigned_uid"
					label={t('DETAILS_ORDERS_FIELD_ASSIGNED_USER')}
					module="users"
					filters={[{ filt_field: 'user_role_external', filt_clause: 'not_equal', filt_value: 1 }]}
					uid={props.model.ords_user_assigned_uid}
					isEdit={props.isEdit && canEditFull}
					sendChange={props.sendChange}
					logProps={props.logProps}
				/>
			</Grid>
			{/* ASSIGNED USER 2 */}
			<Grid size={{ sm: 6, xs: 12 }}>
				<ModuleSearchField
					name="ords_user_assigned_2_uid"
					label={t('DETAILS_ORDERS_FIELD_ASSIGNED_USER_2')}
					module="users"
					filters={[{ filt_field: 'user_role_external', filt_clause: 'equals', filt_value: 1 }]}
					uid={props.model.ords_user_assigned_2_uid}
					isEdit={props.isEdit && canEditFull}
					sendChange={props.sendChange}
					logProps={props.logProps}
				/>
			</Grid>
		</>
	);
}
